'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchQuery } from '@/utils/fetchQuery';
import { GET_POSTS_BY_CATEGORY } from '@/lib/queries/post/category/related';
import { ArrowLeft2 } from 'iconsax-react';
import { ImageNode } from '@/types/global';
import { LoadingSpinner } from '@/components/Icons';
import Link from 'next/link';
import Image from 'next/image';

interface Post {
    databaseId: number;
    title: string;
    uri: string;
    featuredImage?: ImageNode;
}

interface PostsData {
    nodes: Post[];
    pageInfo?: {
        endCursor: string | null;
        total: number;
    };
}

interface SingleCoinExistRelatedPostProps {
    categoryName: string;
    displayName?: string;
    initialData?: PostsData;
    /** Editors picked this list (panel field «راهنماهای مرتبط»): show it as is, in order, nothing more */
    curated?: boolean;
}

/**
 * The optimizer fetches from the WP panel, which is sometimes slow or returns 502;
 * on failure retry the original file once, then show a placeholder instead of a broken image.
 */
const RelatedPostImage: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
    const [attempt, setAttempt] = useState<'optimized' | 'direct' | 'failed'>('optimized');

    if (attempt === 'failed') {
        return <RelatedPostPlaceholder />;
    }

    return (
        <Image
            key={attempt}
            src={src}
            fill
            unoptimized={attempt === 'direct'}
            sizes='(max-width: 640px) 240px, (max-width: 768px) 260px, 280px'
            alt={alt}
            onError={() => setAttempt((prev) => (prev === 'optimized' ? 'direct' : 'failed'))}
            className='object-cover group-hover:scale-105 transition-transform duration-500'
        />
    );
};

const RelatedPostPlaceholder = () => (
    <div className='w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-600 text-xs'>
        بدون تصویر
    </div>
);

/** The general crypto category: more articles when the coin has no category of its own */
const FALLBACK_CATEGORY = 'cryptocurrency';
const PAGE_SIZE = 8;

const SingleCoinExistRelatedPost: React.FC<SingleCoinExistRelatedPostProps> = ({
    categoryName,
    initialData = { nodes: [], pageInfo: { endCursor: null, total: 0 } },
    curated = false,
}) => {
    const [posts, setPosts] = useState<PostsData>({
        ...initialData,
        nodes: initialData.nodes || [],
    });
    const [visibleCount, setVisibleCount] = useState<number>(
        curated ? initialData.nodes?.length || 0 : 4,
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);

    /*
     * "More" articles come from the coin's own blog category, then the general crypto category,
     * each followed by its own cursor. It used to reuse the cursor of the server's first list (a
     * different, random list) on the coin's category: the answer was empty or repeated, the
     * section still thought there was more, and the observer asked again and again without end.
     * Now: duplicates are skipped, and when a source has nothing new (or a request fails) it is
     * done; when every source is done, the loader disappears.
     */
    const initialMore =
        !curated &&
        ((initialData.pageInfo?.total || 0) > (initialData.nodes?.length || 0) ||
            !!initialData.pageInfo?.endCursor);
    const sources = useRef<string[]>(
        Array.from(new Set([categoryName?.toLowerCase(), FALLBACK_CATEGORY].filter(Boolean))),
    );
    const cursor = useRef<string | null>(null);
    const loadingRef = useRef(false);
    const [exhausted, setExhausted] = useState<boolean>(!initialMore);

    const observerTarget = useRef<HTMLDivElement | null>(null);

    const fetchMore = useCallback(async () => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        setIsLoading(true);
        try {
            const seen = new Set(posts.nodes.map((post) => post.databaseId));
            while (sources.current.length > 0) {
                const category = sources.current[0];
                const data = await fetchQuery<{ posts: PostsData }>(GET_POSTS_BY_CATEGORY, {
                    category,
                    first: PAGE_SIZE,
                    after: cursor.current,
                });
                const received = data?.posts?.nodes || [];
                const fresh = received.filter((post) => post?.uri && !seen.has(post.databaseId));
                const nextCursor = data?.posts?.pageInfo?.endCursor || null;
                if (received.length < PAGE_SIZE || !nextCursor) {
                    sources.current = sources.current.slice(1); // this category is used up
                    cursor.current = null;
                } else {
                    cursor.current = nextCursor;
                }
                if (fresh.length > 0) {
                    setPosts((prev) => ({ ...prev, nodes: [...prev.nodes, ...fresh] }));
                    setVisibleCount((prev) => prev + 4);
                    break;
                }
                // Only repeats or nothing: try the next page / source right away
            }
            if (sources.current.length === 0) setExhausted(true);
        } catch (error) {
            // A failing panel must not be asked again in a loop: stop here
            console.error('Error fetching posts:', error);
            setExhausted(true);
        } finally {
            loadingRef.current = false;
            setIsLoading(false);
        }
    }, [posts.nodes]);

    const handleLoadMore = useCallback(() => {
        // Already loaded but hidden: show 4 more first
        if (visibleCount < posts.nodes.length) {
            setVisibleCount((prev) => prev + 4);
            return;
        }
        if (!exhausted) fetchMore();
    }, [visibleCount, posts.nodes.length, exhausted, fetchMore]);

    const hasMoreToLoad = visibleCount < posts.nodes.length || !exhausted;

    // Keep the latest handler in a ref, so the observer is created once (re-creating it on every
    // loading change fired it again immediately while the loader was on screen)
    const loadMoreRef = useRef(handleLoadMore);
    useEffect(() => {
        loadMoreRef.current = handleLoadMore;
    }, [handleLoadMore]);

    useEffect(() => {
        const target = observerTarget.current;
        if (!target || !hasMoreToLoad) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loadingRef.current) {
                    loadMoreRef.current();
                }
            },
            {
                rootMargin: '0px 200px 0px 200px', // trigger slightly before end of scroll
                threshold: 0.1,
            },
        );

        observer.observe(target);
        return () => observer.disconnect();
    }, [hasMoreToLoad]);

    const displayedPosts = posts.nodes.slice(0, visibleCount);

    if (!posts.nodes || posts.nodes.length === 0) {
        return null;
    }

    return (
        <div className='w-full flex flex-col gap-4'>
            {/* Single Row Horizontal Scroll Container */}
            <div className='w-full overflow-x-auto pb-3 pt-1 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-white/10 scrollbar-track-transparent'>
                <div className='flex flex-nowrap items-stretch gap-4 min-w-full'>
                    {displayedPosts.map((post, index) => {
                        const imageSrc = post.featuredImage?.node?.sourceUrl;
                        // Attachment titles are often just the file name (e.g. "mesqal.png")
                        const imageTitle = post.featuredImage?.node?.title;
                        const imageAlt =
                            post.featuredImage?.node?.altText ||
                            (imageTitle && !/\.(png|jpe?g|webp|gif|avif)$/i.test(imageTitle)
                                ? imageTitle
                                : post.title);

                        return (
                            <Link
                                key={post.databaseId || index}
                                href={post.uri || '#'}
                                prefetch={false}
                                className='group flex flex-col shrink-0 w-[240px] sm:w-[260px] md:w-[280px] bg-white dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1'
                            >
                                <div className='relative w-full aspect-[16/9] overflow-hidden bg-gray-100 dark:bg-white/5'>
                                    {imageSrc ? (
                                        <RelatedPostImage src={imageSrc} alt={imageAlt} />
                                    ) : (
                                        <RelatedPostPlaceholder />
                                    )}
                                    <div className='absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300' />
                                </div>

                                <div className='p-3.5 flex flex-col justify-between flex-1 gap-3'>
                                    <h4 className='text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 line-clamp-2 leading-relaxed transition-colors duration-200'>
                                        {post.title}
                                    </h4>

                                    <div className='flex items-center justify-between text-[11px] text-blue-600 dark:text-blue-400 font-medium pt-2 border-t border-gray-50 dark:border-white/5 mt-auto'>
                                        <span>مطالعه مقاله</span>
                                        <ArrowLeft2
                                            size='14'
                                            className='group-hover:-translate-x-1 transition-transform duration-200'
                                        />
                                    </div>
                                </div>
                            </Link>
                        );
                    })}

                    {/* Infinite Scroll Target Sentinel & Spinner */}
                    {hasMoreToLoad && (
                        <div
                            ref={observerTarget}
                            className='flex shrink-0 w-24 sm:w-32 items-center justify-center bg-gray-50/50 dark:bg-white/5 rounded-2xl border border-dashed border-gray-200 dark:border-white/10'
                        >
                            {isLoading ? (
                                <div className='flex flex-col items-center gap-2 text-xs text-gray-400'>
                                    <LoadingSpinner className='size-5 animate-spin text-blue-600 dark:text-white' />
                                    <span>بارگذاری...</span>
                                </div>
                            ) : (
                                <span className='text-xs text-gray-400'>در حال دریافت...</span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SingleCoinExistRelatedPost;
